// Écran « Pilotage » (conception, § 7) : activité, taux de réponse, affaires oubliées ou bloquées, passages
// d'étape, prescriptions. Chaque chiffre qui a sa liste filtrée y mène.
import { useQuery } from "@tanstack/react-query";
import { useLocaleState, useTranslate } from "ra-core";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { Card, CardContent } from "@/components/ui/card";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { today } from "./nextAction";
import { type PilotOverview, replyRate, weeksTable } from "./pilotage";
import { formatDay, formatPartialDate } from "./projectUtils";
import { useDealPipeline } from "./usePartnerOrg";

const dealsLink = (filter: object) =>
  `/deals?filter=${encodeURIComponent(JSON.stringify(filter))}`;

const Block = ({ title, children }: { title: string; children: ReactNode }) => (
  <Card>
    <CardContent className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </CardContent>
  </Card>
);

export const PilotagePage = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const { dealStages } = useDealPipeline();
  const t = (key: string, options?: object) =>
    translate(`resources.deals.pilot.${key}`, options);
  const { data, isPending, isError } = useQuery({
    queryKey: ["pilot_overview"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc("pilot_overview");
      if (error) throw error;
      return data as PilotOverview;
    },
  });
  if (isPending)
    return <p className="mt-4 text-sm text-muted-foreground">{t("loading")}</p>;
  if (isError || !data)
    return <p className="mt-4 text-sm text-destructive">{t("error")}</p>;
  const table = weeksTable(data.weeks);
  const stageLabel = (value: string) =>
    dealStages.find((stage) => stage.value === value)?.label ?? value;
  // Les affaires d'avant le journal n'ont pas d'événement d'étape : le dire à côté des chiffres concernés.
  const since = (
    <p className="text-xs text-muted-foreground">
      {data.journal_since
        ? t("stuck_since", { date: formatDay(data.journal_since, locale) })
        : t("stuck_since_none")}
    </p>
  );

  return (
    <div className="mt-2 pb-6 flex flex-col gap-4">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">{t("title")}</h2>
        <p className="text-sm text-muted-foreground">
          {data.everyone ? t("intro_all") : t("intro_mine")}
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Block title={t("without_action")}>
          <Link
            to={dealsLink({ "next_action_date@is": "null" })}
            className="text-3xl font-bold underline-offset-4 hover:underline"
          >
            {data.without_action}
          </Link>
        </Block>
        <Block title={t("overdue")}>
          {!data.overdue.length ? (
            <p className="text-3xl font-bold">0</p>
          ) : (
            <ul className="text-sm">
              {data.overdue.map((o) => (
                <li key={o.sales_id} className="flex justify-between gap-2">
                  <span>{o.name}</span>
                  <Link
                    className="font-semibold underline-offset-4 hover:underline"
                    to={dealsLink({
                      sales_id: o.sales_id,
                      "next_action_date@lt": today(),
                    })}
                  >
                    {o.count}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Block>
        <Block title={t("stuck")}>
          <p className="text-3xl font-bold">{data.stuck}</p>
          {since}
        </Block>
      </div>
      <Block title={t("reply_rates")}>
        {!data.reply_rates.length ? (
          <p className="text-sm text-muted-foreground">{t("none")}</p>
        ) : (
          <ul className="text-sm">
            {data.reply_rates.map((r) => (
              <li key={r.kind} className="flex justify-between gap-2">
                <span>{translate(`resources.deals.journal.kinds.${r.kind}`)}</span>
                <span className="font-semibold">
                  {replyRate(r.sent, r.replied) ?? "—"} % ({r.replied}/{r.sent})
                </span>
              </li>
            ))}
          </ul>
        )}
      </Block>
      <Block title={t("activity")}>
        {!table.rows.length ? (
          <p className="text-sm text-muted-foreground">{t("none")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-sm">
              <thead>
                <tr>
                  <th />
                  {table.weeks.map((week) => (
                    <th key={week} className="px-2 text-xs font-normal text-muted-foreground">
                      {formatDay(week, locale)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row) => (
                  <tr key={row.name}>
                    <td className="pr-3">{row.name}</td>
                    {row.counts.map((count, i) => (
                      <td key={table.weeks[i]} className="px-2 text-center">
                        {count}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Block>
      <div className="grid gap-4 md:grid-cols-3">
        <Block title={t("conversion")}>
          {!data.conversion.length ? (
            <p className="text-sm text-muted-foreground">{t("none")}</p>
          ) : (
            <ul className="text-sm">
              {data.conversion.map((c) => (
                <li key={c.stage} className="flex justify-between gap-2">
                  <span>{stageLabel(c.stage)}</span>
                  <span>{t("conversion_line", { reached: c.reached, entered: c.entered })}</span>
                </li>
              ))}
            </ul>
          )}
          {since}
        </Block>
        <Block title={t("prescriptions")}>
          {!data.prescriptions.length ? (
            <p className="text-sm text-muted-foreground">{t("none")}</p>
          ) : (
            <ul className="text-sm">
              {data.prescriptions.map((q) => (
                <li key={q.quarter} className="flex justify-between gap-2">
                  <span>{formatDay(q.quarter, locale)}</span>
                  <span className="font-semibold">{q.count}</span>
                </li>
              ))}
            </ul>
          )}
          {since}
        </Block>
        <Block title={t("created")}>
          {!data.created.length ? (
            <p className="text-sm text-muted-foreground">{t("none")}</p>
          ) : (
            <ul className="text-sm">
              {data.created.map((m) => (
                <li key={m.month} className="flex justify-between gap-2">
                  <span>{formatPartialDate(m.month.slice(0, 7), locale)}</span>
                  <span className="font-semibold">{m.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Block>
      </div>
    </div>
  );
};

PilotagePage.path = "/pilotage";
